import styled, { keyframes } from "styled-components"
import { IoWarningOutline } from "react-icons/io5"
import * as CommonTypes from "../../../types/common.types"
import { mobile } from "../../../styles/responsive"

interface RemarksNoticeInterface {
    tourId: string
    action: 'assign' | 'traspass'
    reservations: CommonTypes.FullReservation[]
    onConfirm: () => void
    onCancel: () => void
}

// Reservas que traen algo que la administradora tiene que leer antes de asignar o traspasar:
// el remark general del pasajero o el remark que el vendedor dejó para este tour.
export function withRemarks(reservations: CommonTypes.FullReservation[]) {
    return reservations.filter(r => r.passenger?.remark?.trim() || r.seller_remark?.trim())
}

// Aviso que hay que confirmar antes de abrir Assign / Traspass. No muestra reservations.remark:
// ese lo escribe la propia administradora para la lista impresa del guía.
export default function RemarksNotice({ tourId, action, reservations, onConfirm, onCancel }: RemarksNoticeInterface) {
    return (
        <Main>
            <Modal role="alertdialog" aria-labelledby="remarks-notice-title">
                <h2 id="remarks-notice-title"><IoWarningOutline/> Remarks antes de {action === 'assign' ? 'asignar' : 'traspasar'}</h2>
                <p className="tour">{tourId}</p>
                <TableWrapper>
                    <table>
                        <thead>
                            <tr>
                                <th>Pasajero</th>
                                <th>Remark general</th>
                                <th>Remark de este tour</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                reservations.map(reservation => (
                                    <tr key={reservation.id}>
                                        <td className="name">{reservation.passenger.name}</td>
                                        <td>{reservation.passenger.remark?.trim() || <span className="empty">—</span>}</td>
                                        <td>{reservation.seller_remark?.trim() || <span className="empty">—</span>}</td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </TableWrapper>
                <div className="actions">
                    <button type="button" className="cancel" onClick={onCancel}>Cancelar</button>
                    <button type="button" className="confirm" onClick={onConfirm}>Leído, continuar</button>
                </div>
            </Modal>
        </Main>
    )
}

const Main = styled.div`
z-index: 100;
position: fixed;
top: 0;
left: 0;
width: 100vw;
height: 100vh;
background-color: rgba(0,0,0,0.45);
`

const showUpModal = keyframes`
    from {
        top: 100%;
    }
    to {
        top: 50%;
    }
`

const Modal = styled.div`
width: 90%;
max-width: 720px;
max-height: 85%;
display: flex;
flex-direction: column;
position: absolute;
top: 50%;
left: 50%;
transform: translate(-50%, -50%);
background-color: ${props => props.theme.white};
color: ${props => props.theme.black};
border-top: 8px solid ${props => props.theme.yellowDark};
border-radius: 10px;
box-shadow: 0 0 20px ${props => props.theme.blackTransparent};
padding: 20px 25px;
animation: ${showUpModal} .3s ease forwards;
${mobile({
    width: '96%',
    padding: '15px'
})}
h2 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 20px;
    font-weight: 500;
    svg {
        font-size: 26px;
        flex-shrink: 0;
    }
}
.tour {
    margin: 4px 0 15px;
    font-size: 14px;
    color: ${props => props.theme.gray};
}
.actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
    button {
        height: 2.6em;
        padding: 0 16px;
        font-size: 16px;
        font-family: inherit;
        border-radius: 6px;
        cursor: pointer;
    }
    .cancel {
        background-color: ${props => props.theme.white};
        color: ${props => props.theme.black};
        border: 2px solid ${props => props.theme.black};
    }
    .confirm {
        background-color: ${props => props.theme.blue};
        color: ${props => props.theme.white};
        border: 2px solid ${props => props.theme.blue};
        font-weight: 500;
    }
}
`

const TableWrapper = styled.div`
overflow: auto;
border: 1px solid #e5e7eb;
border-radius: 8px;
table {
    width: 100%;
    border-collapse: collapse;
}
th {
    position: sticky;
    top: 0;
    text-align: left;
    font-weight: 500;
    font-size: 13px;
    padding: 10px 12px;
    background-color: #f9fafb;
    border-bottom: 2px solid #e5e7eb;
    white-space: nowrap;
}
td {
    text-align: left;
    vertical-align: top;
    padding: 10px 12px;
    font-size: 15px;
    border-bottom: 1px solid #f0f0f0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}
td.name {
    font-weight: 500;
    min-width: 130px;
}
tr:last-child td {
    border-bottom: none;
}
.empty {
    color: ${props => props.theme.gray};
}
`
